import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { font } from '../../utils/fontHelper';

interface ShareTabOption {
  label: string;
  value: string;
  icon?: React.ReactNode;
}

interface CustomShareTabsProps {
  value: string;
  onChange: (newValue: string) => void;
  tabs: ShareTabOption[];
  sx?: any;
}

const CustomShareTabs: React.FC<CustomShareTabsProps> = ({ value, onChange, tabs, sx = {} }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box sx={{
      display: 'flex',
      gap: 1, // 8px gap from Figma
      ...sx
    }}>
      {tabs.map((tab) => {
        const isSelected = value === tab.value;
        
        return (
          <Box
            key={tab.value}
            onClick={() => onChange(tab.value)}
            sx={{
              px: 1.5, // 12px padding from Figma
              py: 0,
              height: 40, // Fixed 40px height from Figma
              minWidth: 'auto',
              borderRadius: '8px', // 8px border radius from Figma
              border: isSelected 
                ? `1px solid ${isDark ? 'rgba(255,255,255,0.3)' : 'rgba(60,60,67,0.3)'}` 
                : `1px solid ${isDark ? 'rgba(255,255,255,0.3)' : 'rgba(60,60,67,0.3)'}`,
              bgcolor: isSelected 
                ? (isDark ? '#FFFFFF' : '#000000') 
                : 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5, // 4px gap between icon and text from Figma
              transition: 'all 0.2s ease',
              flex: '0 0 auto',
              '&:hover': {
                bgcolor: isSelected 
                  ? (isDark ? '#FFFFFF' : '#000000') 
                  : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'),
              }
            }}
          >
            {/* Icon */}
            {tab.icon && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 20,
                color: isSelected 
                  ? (isDark ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)') 
                  : (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
                '& svg': {
                  fontSize: 20,
                }
              }}>
                {tab.icon}
              </Box>
            )}
            
            {/* Text */}
            <Typography sx={{
              fontFamily: font(), // Inter font from Figma
              fontWeight: isSelected ? 500 : 400, // Font weight from Figma design
              fontSize: 14, // 14px font size from Figma
              lineHeight: 1.428, // ~20px line height for 14px font
              color: isSelected
                ? (isDark ? '#000000' : '#FFFFFF')
                : (isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.9)'),
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'center',
            }}>
              {tab.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default CustomShareTabs;